import React from 'react';
import type { ReactElement, MouseEvent } from 'react';

import { CloseIcon } from '@/assets/icons/v2/CloseIcon';
import { DraggablePlayer } from './components/Draggable';
import { useVideoPlayerContext } from './context';

import * as S from './styles';

interface VideoPlayerDraggableProps {
	children: JSX.Element | JSX.Element[];
	onClose?: () => void;
	getContainer?: () => HTMLElement;
	onClick?: (event: MouseEvent) => void;
	onMouseEnter?: (event: MouseEvent) => void;
	onMouseLeave?: (event: MouseEvent) => void;
}

function handleRenderCloseIcon(fill?: string): ReactElement {
	return (
		<S.HoverIcon>
			<CloseIcon width={10} height={13} fill={fill} />
		</S.HoverIcon>
	);
}

export function VideoPlayerDraggable({
	children,
	onClose,
	onClick,
	getContainer,
	onMouseEnter,
	onMouseLeave
}: Readonly<VideoPlayerDraggableProps>): ReactElement {
	const { playingState, handleCloseClick, handleExpandClick, handleVideoMouseEnter, handleVideoMouseLeave } =
		useVideoPlayerContext();

	const closeIcon = handleRenderCloseIcon(playingState ? undefined : '#505050');

	function handleClose(): void {
		handleCloseClick();
		onClose?.();
	}

	function handleClick(event: MouseEvent): void {
		handleExpandClick(event);
		onClick?.(event);
	}

	function handleMouseEnterEvent(event: MouseEvent): void {
		handleVideoMouseEnter();
		onMouseEnter?.(event);
	}

	function handleMouseLeaveEvent(event: MouseEvent): void {
		handleVideoMouseLeave();
		onMouseLeave?.(event);
	}

	return (
		<DraggablePlayer
			closeIcon={closeIcon}
			onClose={handleClose}
			onClick={handleClick}
			getContainer={getContainer}
			onMouseEnter={handleMouseEnterEvent}
			onMouseLeave={handleMouseLeaveEvent}
		>
			{children}
		</DraggablePlayer>
	);
}
